/**
 * 작성자 : 홍선영
 * 날짜 : 2024.12.05
 * 기능 : (사내)업무관리-근태관리-근태신청 페이지 신청 폼
 */

import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userState } from '../../../atoms';
import GetFetchStatus from '../../../hooks/GetFetchStatus';
import ApplicationFormItems from '../../c_setting/setting7/ApplicationFormItems';
import styled from 'styled-components';
import { useSetAuth } from '../../../utils/useSetAuth';
import { BtnBlue, BtnGhost, BtnRed, BtnYellow } from '../../../components/Button';
import { useTranslation } from 'react-i18next';
import IssueGuide from '../../../components/IssueGuide';
import TextInfoBox from '../../../components/TextInfoBox';
import SmallTextLabel from '../../../components/button/SmallTextLabel';
import Portal from '../../../components/Portal';
import ApprovalModal from '../../../components/Modal/ApprovalModal';
import MultiSelect from '../../../components/MultiSelect';
import { AttendanceD, IApplicationFormType, IAttendance } from 'customTypes';
import { renderApprovalStatus, renderLineStatus } from '../../../utils/renderApprovalStatus';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../../services/_common';
import { toast } from 'react-toastify';
import DeleteModal from '../../../components/Modal/DeleteModal2';
import ReplyRow from './ReplyRow';
import { isImageValid } from '../../../utils/isImageValid';
import { PrintContextConsumer, useReactToPrint } from 'react-to-print';
import { isPhoneNumRegExpPass } from '../../../utils/regExp';
import { getCurrentDateTimeString } from '../../../utils/formatDate';
import FileUploadButton from '../../../components/button/FileUploadButton';
import { logPost } from '../../../services/log';
import { downloadFile, setupFlutterFileHandlers } from '../../../utils/flutterBridge';
import { ImagePreview, PhotoImage } from '../../../components/ImagePreview';
import FormatImg from '../../../components/FormatImg';
import { isImageFile } from '../../../utils/formatFilePath';
import { showPrintButton } from '../../../utils/deviceAuth';
import { isMobile } from '../../../utils/checkMobile';
import { getIsStackedFormLayout, getStackedCells, isStackedAutoHeight } from '../../c_setting/setting7/stackedFormLayout';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PulseLoader } from 'react-spinners';

interface Cell {
  cdName: string;
  width: number;
  height: number;
  aRow: number | null;
  aCol: number | null;
  isDropped: boolean; // Drop 여부
  subCd: string;
  aCd: string;
  aContents: string;
}

interface IUserType {
  label: string;
  value: string;
}

interface DropZoneProps {
  cells: Cell[];
  setCells: React.Dispatch<React.SetStateAction<Cell[]>>;
  logo: string;
  rowState: IAttendance;
  userInput: any;
  setUserInput: React.Dispatch<React.SetStateAction<any>>;
  formType: IApplicationFormType;
  approvalType: 'approval' | 'request';
  isSaveClicked: boolean;
  selectedAttD1List: { label: string; value: string }[];
  className?: string;
  editorContent: string;
  setEditorContent: React.Dispatch<SetStateAction<string>>;
}

const GRID_COL_COUNT = 3;
const CELL_HEIGHT = 46;
const PENDING_APPROVAL = '1'; // 보류
const APPROVED = '2'; // 승인
const REJECTED = '3'; // 반려
const WAITING = '0'; // 결재 대기
// 승인·반려한 결재자는 다음 결재자가 처리하기 전까지 수정 버튼으로 다시 처리할 수 있다 (보류는 이미 본인 차례라 제외)
const REVISABLE_LSTATUS = [APPROVED, REJECTED];
const INIT_ROW = {
  wDate: '',
  eDate: '',
  cCd: '',
  caCd: '',
  caStatus: '0',
  status: '0',
  caName: '',
  caDepartment: '',
  emerNum: '',
  address: '',
  caGubun: '',
  sDate: '',
  fDate: '',
  caPdate: '',
  caPtime: '',
  caReason: '',
  caReplacement: '',
  delYn: '',
  writer: '',
  editor: '',
  attendanceD1: [],
  attendanceD2: [],
  creportLine: [],
  cdraftD1: [],
  cdraftD2: [],
  currentUserId: '',
  signImg: '',
  uAnnualnum: '0',
  uRannualnum: '0',
  uPannualnum: '0',
  content: '',
  date: '',
  note: '',
  files: [],
};

// 실제 데이터가 있는 행의 수를 계산
const calculateActualRowCount = (cells: Cell[]): number => {
  const droppedCells = cells.filter((cell) => cell.isDropped);
  if (droppedCells.length === 0) return 0;

  // 가장 마지막 행 위치 찾기 (aRow가 null이 아닌 셀들 중)
  const maxRow = Math.max(...droppedCells.map((cell) => (cell.aRow !== null ? cell.aRow + cell.height : 0)));
  return maxRow;
};

const DropZone: React.FC<DropZoneProps> = ({
  cells,
  setCells,
  logo,
  rowState,
  userInput,
  setUserInput,
  formType,
  approvalType,
  isSaveClicked,
  selectedAttD1List,
  className = '',
  editorContent = '',
  setEditorContent,
}) => {
  const actualRowCount = calculateActualRowCount(cells); // 실제 행 수 계산
  const rowCount = actualRowCount > 0 ? actualRowCount : 1; // 최소 1행은 보여주기
  // 모바일(화면 폭 767px 이하) 근태계는 항목을 한 줄에 하나씩 배치. 인쇄본·보고서(creport)는 격자 배치 유지
  const [isStacked] = useState(() => className !== 'print' && formType === 'attendance' && getIsStackedFormLayout());

  if (isStacked) {
    return (
      <div className='dropzone-stack' style={{ display: 'flex', flexDirection: 'column', padding: '0.5rem', border: '1px solid lightgray', borderRadius: '3px', margin: '0.2rem' }}>
        {getStackedCells(cells, rowCount, GRID_COL_COUNT).map((cell) => (
          <DraggableCell
            key={`${cell.aRow}_${cell.aCol}`}
            cell={cell}
            setCells={setCells}
            logo={logo}
            rowState={rowState}
            userInput={userInput}
            setUserInput={setUserInput}
            formType={formType}
            approvalType={approvalType}
            isSaveClicked={isSaveClicked}
            selectedAttD1List={selectedAttD1List}
            className={className}
            editorContent={editorContent}
            setEditorContent={setEditorContent}
            stacked
          />
        ))}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${GRID_COL_COUNT}, 33.3%)`,
        gridTemplateRows: `repeat(${rowCount}, ${CELL_HEIGHT}px)`,
        padding: '0.5rem',
        overflowY: 'auto',
        border: `${className === 'print' ? '1px solid #18181b' : '1px solid lightgray'}`,
        borderBottom: `${className === 'print' ? '2px solid #18181b' : '1px solid lightgray'}`,
        borderRadius: `${className === 'print' ? '0' : '3px'}`,
        margin: '0.2rem',
      }}
    >
      {Array.from({ length: GRID_COL_COUNT * rowCount }).map((_, index) => {
        const row = Math.floor(index / GRID_COL_COUNT); // 열 개수를 기준으로 계산
        const col = index % GRID_COL_COUNT; // 열 인덱스 계산
        const cell = cells.find((c) => c.aRow === row && c.aCol === col);

        return (
          <div
            key={index}
            style={{
              width: '100%',
              height: `${CELL_HEIGHT}px`,
              position: 'relative',
            }}
          >
            {cell && (
              <DraggableCell
                cell={cell}
                setCells={setCells}
                logo={logo}
                rowState={rowState}
                userInput={userInput}
                setUserInput={setUserInput}
                formType={formType}
                approvalType={approvalType}
                isSaveClicked={isSaveClicked}
                selectedAttD1List={selectedAttD1List}
                className={className}
                editorContent={editorContent}
                setEditorContent={setEditorContent}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const DraggableCell: React.FC<{
  cell: Cell;
  setCells: React.Dispatch<React.SetStateAction<Cell[]>>;
  logo: string;
  rowState: IAttendance;
  userInput: any;
  setUserInput: Dispatch<SetStateAction<any>>;
  formType: IApplicationFormType;
  approvalType: 'approval' | 'request';
  isSaveClicked: boolean;
  selectedAttD1List: { label: string; value: string }[];
  className?: string;
  editorContent: string;
  setEditorContent: Dispatch<SetStateAction<string>>;
  stacked?: boolean; // 모바일 한 줄 배치 여부
}> = ({ cell, setCells, logo, rowState, userInput, setUserInput, formType, approvalType, isSaveClicked, selectedAttD1List, className, editorContent, setEditorContent, stacked = false }) => {
  return (
    <div
      className={`draggable-cell subcd_${cell.subCd}`}
      style={{
        position: 'relative',
        width: stacked ? '100%' : cell.isDropped ? `${cell.width * 100}%` : `calc((100% / ${GRID_COL_COUNT}) * ${cell.width})`, // 동적으로 width 계산
        // maxWidth: `${cell.width === 3 ? `calc(${cell.width * 100}% - 2rem)` : `${cell.width * 100}%`}`,
        // height는 고정된 그리드 높이에 비례. 한 줄 배치에서 입력·글자 항목은 내용 높이만큼
        height: stacked && isStackedAutoHeight(cell.subCd) ? 'auto' : `${CELL_HEIGHT * cell.height}px`,
        display: 'flex',
        // justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
        fontWeight: 'bold',
        // zIndex: 1,
      }}
    >
      {/** 근태 처리 상태가 상신이면서 근태신청 컴포넌트인 경우에만 수정 가능 */}
      {ApplicationFormItems(
        {
          type:
            ((formType === 'attendance' && rowState.caStatus === '0') || (formType === 'cdraft' && rowState.status === '0') || formType === 'creport') && approvalType === 'request' ? 'edit' : 'view',
          data: rowState,
          formType,
        },
        cell.subCd,
        cell.cdName,
        cell,
        setCells,
        logo,
        userInput,
        setUserInput,
        isSaveClicked,
        selectedAttD1List,
        stacked ? 'stacked' : className, // 항목 스타일(ApplicationFormItems Root)에 한 줄 배치 클래스 전달
        editorContent,
        setEditorContent
      )}
    </div>
  );
};

interface IForm {
  formType: IApplicationFormType;
  approvalType: 'request' | 'approval';
  rowState: IAttendance;
  setRowState: Dispatch<SetStateAction<IAttendance>>;
  isNewAdd: boolean;
  setIsNewAdd: Dispatch<SetStateAction<boolean>>;
  tableState: IAttendance[] | null;
  setInitTableState: Dispatch<SetStateAction<IAttendance[] | null>>;
  formatInTuiForm: any;
}

interface IFile {
  file: File;
  fileName: string;
  fileFormat: string;
  fUrl: string;
  fIndex: number;
}

const INIT_SCHEDULE: IFile[] = [{ fIndex: 0, file: new File([], ''), fileName: '', fileFormat: '', fUrl: '' }];
const MAX_SELECT_COUNT = 6; // 결재라인 최대 선택 가능한 개수
const S3_URL = process.env.REACT_APP_AWS_ROOT_URL;
const DIRECTORY_NAME = process.env.REACT_APP_AWS_DIRECTORY_NAME;
const mobile = isMobile();

const Form = ({ formType, approvalType, rowState, setRowState, isNewAdd, setIsNewAdd, tableState, setInitTableState, formatInTuiForm }: IForm) => {
  const statusKey = 'caStatus';
  const { t } = useTranslation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const userInfo = useRecoilValue(userState);
  const queryClient = useQueryClient();
  const [cells, setCells] = useState<Cell[]>([]);
  const [logo, setLogo] = useState<string>('');
  const [openModal, setOpenModal] = useState({ type: '', status: false });
  const [users, setUsers] = useState<{ label: string; value: string }[]>([]); // 사내 전체 유저
  const [selectedAttD1List, setSelectedAttD1List] = useState<{ label: string; value: string }[]>([]); // 결재라인 선택목록
  const [selectedAttD2List, setSelectedAttD2List] = useState<{ label: string; value: string }[]>([]); // 수신참조 선택목록
  const [filesState, setFilesState] = useState(INIT_SCHEDULE); // 선택된 일정 데이터
  const [deleteFileIndex, setDeleteFileIndex] = useState<number[]>([]); // 삭제된 파일 인덱스

  const { data: formSet, isError: formSetError } = GetFetchStatus({ path: `/${formType}/set`, req: { cCd: userInfo.hCd }, useLoginUserInfo: false });
  const { data: cInfo } = GetFetchStatus({ path: '/company/info/one', req: { cCd: userInfo.hCd }, useLoginUserInfo: false });
  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false); // 필수값 여부
  const [isApprovalSubmitting, setIsApprovalSubmitting] = useState(false);
  const approvalSubmittingRef = useRef(false);
  const [isRevisingApproval, setIsRevisingApproval] = useState(false); // 종결된 근태를 최종 결재자가 다시 처리하는 중인지
  const [userInput, setUserInput] = useState({
    caName: '',
    caDepartment: '',
    emerNum: '',
    address: '',
    caGubun: '',
    caReason: '',
    caReplacement: '',
    wDate: '',
    sDate: '',
    fDate: '',
    caStatus: '0',
    approvalLine: [{ label: '', value: '' }],
    signImg: '',
    caPdate: '',
    caPtime: '',
    title: '',
    content: '',
    name: '',
    department: '',
    note: '',
  });
  const [editorContent, setEditorContent] = useState('');
  const { data: attendanceComments } = GetFetchStatus({
    path: `/${formType}/comment`,
    req: { cCd: userInfo.hCd, caCd: rowState?.caCd },
    queryKey: rowState?.caCd,
    useLoginUserInfo: false,
    enabledKey: rowState.caCd !== '',
  });
  const showPrintBtn = showPrintButton(auth.printAuth);

  const { data: cUserList, isLoading: cUserListLoading } = useQuery(['/user/company', userInfo.hCd], () => fetchCompanyUser(), {
    enabled: !!userInfo.hCd && userInfo.hCd?.substring(0, 1)?.toLowerCase() === 'c',
  });

  const fetchCompanyUser = async () => {
    try {
      const res = await apiGet({ path: '/user/company', req: { hCd: userInfo.hCd } });
      const newMap = res.data.data
        .filter((el: any) => el.uWorkstatus === 'I')
        .map((el: any) => ({ label: el.userName, value: el.userId, positionName: el.positionName, partName: el.partName }))
        .filter((el2: any) => el2.value !== userInfo.userId);
      setUsers(newMap);
      return newMap;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  useEffect(() => {
    setIsRevisingApproval(false);
  }, [rowState.caCd]);

  /** 결재 처리 후에는 문서 번호(caCd)가 같아도 결재선의 상태·일자가 바뀐다.
      이 값까지 의존성에 넣어야 결재 직후 결재란이 새 정보로 다시 그려진다 */
  const approvalLineSnapshot = (rowState[formType === 'attendance' ? 'attendanceD1' : 'cdraftD1'] ?? []).map((line: AttendanceD) => `${line.userId}:${line.caLstatus}:${line.caDate}`).join('|');

  useEffect(() => {
    if (isSaveClicked) setIsSaveClicked(false);

    let isCancelled = false;

    const fetchData = async () => {
      const { list1, list2 } = await getApprovalLineUsers(rowState);
      // 비동기 작업이 완료된 후에도 컴포넌트가 마운트되어 있고 취소되지 않았을 때만 상태 업데이트
      if (!isCancelled) {
        setSelectedAttD1List(list1 ?? []);
        setSelectedAttD2List(list2 ?? []);
        setEditorContent(rowState.content);
      }
    };
    fetchData();

    setFilesState(
      rowState.files.map((el: any) => {
        const splitUrl = el.fUrl.split('/');
        const fileName = splitUrl[splitUrl.length - 1];
        return { fIndex: el.fIndex, file: el.file, fileName, fileFormat: el.fileFormat, fUrl: el.fUrl };
      })
    );

    // 클린업 함수: 컴포넌트가 언마운트되거나 의존성이 변경되면 이전 비동기 작업 취소
    return () => {
      isCancelled = true;
    };
  }, [rowState.caCd, approvalLineSnapshot, cUserList]);

  const getApprovalLineUsers = async (row: IAttendance) => {
    const approvalLineKey = formType === 'creport' ? 'creportLine' : formType === 'attendance' ? 'attendanceD1' : 'cdraftD1';
    const approvalLineKey2 = formType === 'creport' ? 'creportLine' : formType === 'attendance' ? 'attendanceD2' : 'cdraftD2';

    const insertSignPromises =
      row[approvalLineKey]?.map(async (el: AttendanceD) => {
        const isValid = await isImageValid(`${S3_URL}${DIRECTORY_NAME}${userInfo.hCd}/sign/${el.userId}.png`);
        return {
          label: el.userName,
          value: el.userId,
          caDate: el.caDate,
          caLstatus: el.caLstatus,
          partName: el.partName,
          positionName: el.positionName,
          signImg: isValid ? `${S3_URL}${DIRECTORY_NAME}${userInfo.hCd}/sign/${el.userId}.png` : '',
        };
      }) ?? [];

    const insertSign = insertSignPromises.length > 0 ? await Promise.all(insertSignPromises) : [];
    const list2 = row[approvalLineKey2]?.map((el: AttendanceD) => ({
      label: el.userName,
      value: el.userId,
      caDate: el.caDate,
      caLstatus: el.caLstatus,
      positionName: el.positionName,
    }));

    return { list1: insertSign, list2 };
  };

  useEffect(() => {
    if (cInfo) setLogo(cInfo[0]?.cLogoimage);
  }, [cInfo]);

  useEffect(() => {
    if (formSet) {
      const set = formSet.map((el: Cell) => ({ ...el, isDropped: el.aCol !== null && el.aRow !== null, height: Number(el.height), width: Number(el.width) }));
      setCells(set);
    }
  }, [formSet]);

  const checkAttendanceMandatoryField = () => {
    setIsSaveClicked(true);
    const { emerNum, address, caGubun, sDate, fDate, caReason, caReplacement } = userInput;

    if (userInfo.gCd?.substring(0, 1)?.toLowerCase() !== 'c') {
      toast.warning(t('등록된 사내유저만 이용 가능합니다.'));
      return false;
    }

    if (rowState[statusKey] !== '0') {
      toast.warning(t('상신 상태에서만 수정 가능합니다.'));
      return false;
    }

    /**
     * 수정자 : 홍선영
     * 수정일자 : 2026.07.27
     * 수정내용 : 잔여 연차가 부족해도 근태 상신 가능하도록 차단 제거 (연차 마이너스 사용 허용)
     *           2025.02.24 자 '마이너스 사용 불가' 처리를 되돌림.
     *           백엔드도 validateAnnualnum() 호출이 주석 처리되어 있어 서버 차단 없음.
     */

    if (selectedAttD1List.length === 0) {
      toast.warning(t('결재 라인을 선택하세요'));
      return false;
    }

    if (emerNum?.trim() === '') {
      toast.warning(t('비상연락처를 입력하세요'));
      return false;
    }

    if (!isPhoneNumRegExpPass(emerNum)) {
      toast.warning(t('올바른 전화번호 형식이 아닙니다'));
      return false;
    }

    if (address?.trim() === '') {
      toast.warning(t('주소를 입력하세요'));
      return false;
    }

    if (caGubun === '') {
      toast.warning(t('근태 구분을 선택하세요'));
      return false;
    }

    if (caReason?.trim() === '') {
      toast.warning(t('사유를 입력하세요'));
      return false;
    }

    if (caReplacement?.trim() === '') {
      toast.warning(t('업무 대체자를 입력하세요'));
      return false;
    }

    return true;
  };

  const checkReportMandatoryField = () => {
    if (userInfo.gCd?.substring(0, 1)?.toLowerCase() !== 'c') {
      toast.warning(t('등록된 사내유저만 이용 가능합니다.'));
      return false;
    }
    return true;
  };

  const checkDraftMandatoryField = () => {
    if (userInfo.gCd?.substring(0, 1)?.toLowerCase() !== 'c') {
      toast.warning(t('등록된 사내유저만 이용 가능합니다.'));
      return false;
    }

    if (rowState[statusKey] !== '0') {
      toast.warning(t('상신 상태에서만 수정 가능합니다.'));
      return false;
    }

    if (selectedAttD1List.length === 0) {
      toast.warning(t('결재 라인을 선택하세요'));
      return false;
    }

    return true;
  };

  const getReq = (reqData: any) => {
    const formData = new FormData();
    Object.keys(reqData).forEach((key) => {
      key === 'attendanceD1'
        ? formData.append(key, JSON.stringify(reqData[key]))
        : key === 'attendanceD2'
        ? formData.append(key, JSON.stringify(reqData[key]))
        : key === 'deleteFileIndex'
        ? formData.append(key, JSON.stringify(reqData[key]))
        : key === 'files'
        ? reqData[key].forEach((file: any) => {
            if (file) formData.append('files', file);
          })
        : formData.append(key, reqData[key]);
    });
    return formData;
  };

  const onClickSaveAttendance = async () => {
    setIsSaveClicked(true);
    const { emerNum, address, caGubun, sDate, fDate, caReason, caReplacement, caPdate, caPtime } = userInput;

    if (formType === 'attendance' && checkAttendanceMandatoryField()) {
      try {
        const attendanceD1 = selectedAttD1List.map((el, i) => ({ caSeq: i.toString(), userId: el.value }));
        const attendanceD2 = selectedAttD2List.map((el) => ({ userId: el.value }));
        const req = {
          // 수정인 경우 caCd 포함
          ...(rowState.caCd && { caCd: rowState.caCd }),
          cCd: userInfo.hCd,
          caName: rowState.caName,
          caDepartment: rowState.caDepartment,
          emerNum: emerNum !== '--' ? emerNum : '',
          address,
          caGubun,
          sDate,
          fDate,
          caPdate,
          caPtime,
          caReason,
          caReplacement,
          attendanceD1,
          attendanceD2,
          files: filesState.filter((el) => el.file).map((el) => el.file),
          deleteFileIndex,
        };
        const newReq = getReq({ ...req });
        let res;
        if (rowState.caCd) {
          res = await apiPatch({ path: '/attendance', req: newReq, contentType: 'multipart/form-data' }); // 업데이트
        } else {
          newReq.delete('deleteFileIndex');
          res = await apiPost({ path: '/attendance', req: newReq, contentType: 'multipart/form-data' }); // 신규 등록
        }

        if (res.data.statusCode === 200) {
          toast.success(t(res.data.message));
          setInitTableState(formatInTuiForm(res.data.data));
          // 신규등록 직후 rowState 업데이트
          if (isNewAdd) {
            setRowState({ ...res.data.data[0], signImg: rowState.signImg });
            setIsNewAdd(false);
            setIsSaveClicked(false);
          }
          await logPost({
            hCd: userInfo.hCd,
            sCd: userInfo.sCd,
            userId: userInfo.userId,
            menu: '근태 관리 > 근태 신청',
            action: '저장',
            etc: `근태 상신`,
          });
        }
      } catch (error) {
        console.error(error);
        throw new Error('error');
      }
    }
  };

  const approvalLineKey = formType === 'attendance' ? 'attendanceD1' : 'cdraftD1';
  // 가장 큰 caSeq 값을 가진 결재자가 최종 결재권자
  const finalApproverLine = rowState[approvalLineKey]?.reduce((max, current) => {
    return current.caSeq > max.caSeq ? current : max;
  }, rowState[approvalLineKey][0]);
  // 유저가 최종 결재권자인지 여부
  const isFinalApprover = finalApproverLine?.userId === userInfo.userId;
  const sortedApprovalLine: AttendanceD[] = [...(rowState[approvalLineKey] ?? [])].sort((a, b) => Number(a.caSeq) - Number(b.caSeq));
  const myLineIndex = sortedApprovalLine.findIndex((line) => line.userId === userInfo.userId);
  const myLine = sortedApprovalLine[myLineIndex];
  /** 내가 승인·반려한 뒤 다음(상위) 결재자가 아직 아무도 처리하지 않았으면 다시 처리할 수 있다.
      최종 결재자는 다음 결재자가 없으므로 본인이 처리했으면 언제든 해당한다. 서버도 같은 기준으로 검사한다 */
  const canReviseApproval =
    formType === 'attendance' &&
    approvalType === 'approval' &&
    !!myLine &&
    REVISABLE_LSTATUS.includes(myLine.caLstatus) &&
    sortedApprovalLine.slice(myLineIndex + 1).every((line) => line.caLstatus === WAITING);
  const isMyApprovalTurn = rowState.currentUserId === userInfo.userId;
  // 현재 내 결재 상태와 같은 버튼은 서버가 중복 요청으로 거절하므로 막는다 (수정 중일 때, 보류 후 내 차례가 남아 있을 때)
  const myCurrentLstatus = myLine?.caLstatus;

  const onClickApproval = async (param: '0' | '1' | '2' | '3') => {
    if (approvalSubmittingRef.current) return;

    const saveStatusKey = formType === 'attendance' ? 'caLstatus' : 'lstatus';
    // 연차 차감 여부(경조/공가/교육의 경우 연차 차감되지 않음)
    const isNonDeductibleLeave = rowState.caGubun === '2' || rowState.caGubun === '3' || rowState.caGubun === '4';
    const expectedCaLstatus = formType === 'attendance' ? rowState.attendanceD1?.find((line) => line.userId === userInfo.userId)?.caLstatus : undefined;

    approvalSubmittingRef.current = true;
    setIsApprovalSubmitting(true);
    try {
      const req = {
        cCd: rowState.cCd,
        caCd: rowState.caCd,
        userId: rowState.writer, // 상신자
        caStatus: rowState.caStatus,
        [saveStatusKey]: param,
        ...(expectedCaLstatus !== undefined && { expectedCaLstatus }),
        finalApproval: isFinalApprover ? '1' : '0', // 최종 결재권자 여부
        ...(formType === 'attendance' && { caPdate: isNonDeductibleLeave ? '0' : rowState.caPdate }),
        ...(formType === 'attendance' && { caPtime: rowState.caPtime }),
      };
      const res = await apiPatch({ path: `/${formType}/history`, req });
      // 403: 결재 차례가 아니거나 다음 결재자가 이미 처리함, 409: 그사이 상태가 바뀜
      if ([403, 409].includes(res?.status) || [403, 409].includes(res?.data?.statusCode)) {
        const conflictMessage = res.data?.message;
        const conflictMessageText = Array.isArray(conflictMessage)
          ? conflictMessage.join(', ')
          : typeof conflictMessage === 'string'
          ? conflictMessage
          : '이미 처리된 결재 요청입니다. 최신 상태를 확인해 주세요.';
        toast.warning(t(conflictMessageText));
        return;
      }

      if (res.data.statusCode === 200) {
        setInitTableState(formatInTuiForm(res.data.data));
        const find = res.data.data.find((el: IAttendance) => el.caCd === rowState?.caCd);
        if (find) {
          // 상신자 서명정보
          const isValidSignImg = await isImageValid(`${S3_URL}${DIRECTORY_NAME}${userInfo.hCd}/sign/${find.writer}.png`);
          const randomString = Math.random().toString(36).substring(7); // 랜덤 문자열을 쿼리스트링으로 추가해서 이미지 캐싱 방지
          setRowState({ ...find, signImg: isValidSignImg ? `${S3_URL}${DIRECTORY_NAME}${userInfo.hCd}/sign/${find.writer}.png?v=${randomString}` : '' });
          setIsRevisingApproval(false);
          toast.success(t(`${renderLineStatus(param).text} 되었습니다`));
        }
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '근태 관리 > 근태 결재',
          action: `${renderLineStatus(param).text} 처리`,
          etc: `근태계 문서 번호 : ${rowState.caCd}`,
        });
        // 사이드메뉴 결재 알림 쿼리 무효화하여 다시 가져오기 (AsideMainMenu)
        queryClient.invalidateQueries({ queryKey: ['approval_attendance_approval', userInfo.hCd] });
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    } finally {
      approvalSubmittingRef.current = false;
      setIsApprovalSubmitting(false);
    }
  };

  const deleteAPI = async () => {
    try {
      const req = { cCd: userInfo.hCd, caCd: rowState.caCd };
      const res = await apiDelete({ path: `/${formType}`, req });
      if (res.data.statusCode === 200) {
        toast.success(t(res.data.message));
        setIsNewAdd(true);
        setRowState(INIT_ROW);
        setFilesState(INIT_SCHEDULE);
        const result = tableState?.filter((el) => el.caCd !== req.caCd) ?? [];
        setInitTableState(formatInTuiForm(result));
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '근태 관리 > 근태 신청',
          action: '삭제',
          etc: `근태계 문서 번호 : ${rowState.caCd}`,
        });
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const onClickDelete = async () => {
    // 업무 보고는 결재 진행 프로세스가 따로 없으므로 제외
    if (formType !== 'creport' && rowState && rowState[statusKey] !== '0') {
      if (rowState[statusKey] === '3') {
        toast.warning(t('완료된 결재 문서는 삭제할 수 없습니다.'));
        return;
      }
      toast.warning(t('결재 진행 중입니다.'));
      return;
    }

    setOpenModal((prev) => ({ ...prev, status: true, type: 'delete', api: deleteAPI }));
  };

  const getLineList = (list1: string[], list2: string[]) => {
    const newList1 = list1.map((el) => users.find((u) => u.value === el)).filter((el2): el2 is IUserType => el2 !== null && el2 !== undefined);
    const newList2 = list2.map((el) => users.find((u) => u.value === el)).filter((el2): el2 is IUserType => el2 !== null && el2 !== undefined);

    setSelectedAttD1List((prev) => {
      const setFilter = new Set([...prev, ...newList1]); // 합친 이후 중복 제거
      return [...setFilter];
    });
    setSelectedAttD2List((prev) => {
      const setFilter = new Set([...prev, ...newList2]); // 합친 이후 중복 제거
      return [...setFilter];
    });
    setOpenModal({ type: '', status: false });
  };

  const onClickPrint = async () => {
    handlePrint();
  };

  const componentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `@page { size: A4; margin: 10mm; } .dropzone-wrapper { zoom: 0.9; }`,
  });

  // 파일 업로드
  const onChangeFileUpload = async (e: ChangeEvent<HTMLInputElement> | undefined) => {
    if (e) {
      const { files } = e.target;
      if (files) {
        const arr: number[] = Array.from({ length: files.length }, (_, i) => i);
        const duplication: IFile[] = filesState.filter((file: IFile) => arr.some((currentIdx: number) => file.fileName === files[currentIdx].name));

        if (filesState.length >= 10) {
          e.target.value = ''; // 파일 필드 초기화
          toast.warn(t('업로드 가능한 파일 갯수는 최대 10개 입니다'));
          return false;
        }
        if (duplication.length > 0) {
          e.target.value = ''; // 파일 필드 초기화
          toast.warn(t('이미 불러온 파일입니다'));
          return false;
        }

        fileDataSet(e, setFilesState);
      }
    } else {
      // Flutter 파일 선택 처리
      try {
        if (window.FilePicker) {
          window.FilePicker.postMessage('all');
        }
      } catch (error) {
        console.error('파일 처리 에러:', error);
      }
    }
    return false;
  };

  useEffect(() => {
    const cleanup = setupFlutterFileHandlers(
      (fileData) => {
        const fileOption: IFile = {
          fIndex: filesState.length,
          file: fileData.file as File,
          fileName: fileData.name,
          fileFormat: fileData.name.slice(fileData.name.lastIndexOf('.') + 1),
          fUrl: '',
        };

        if (filesState.length >= 10) {
          toast.warn(t('업로드 가능한 파일 갯수는 최대 10개 입니다'));
          return;
        }

        const duplication = filesState.find((f: IFile) => f.fileName === fileData.name);
        if (duplication) {
          toast.warn(t('이미 불러온 파일입니다'));
          return;
        }

        setFilesState((prev: IFile[]) => [...prev, fileOption]);
      },
      (error) => {
        console.error('파일 선택 오류:', error);
      },
      undefined,
      'all'
    );

    return cleanup;
  }, [filesState]);

  const fileDataSet = (e: ChangeEvent<HTMLInputElement>, set: any) => {
    const { files } = e.target;

    if (files) {
      const arr: number[] = Array.from({ length: files.length }, (_, i) => i);
      const fileOption: IFile[] = arr.map((currentIdx: number) => ({
        fIndex: currentIdx,
        file: files[currentIdx],
        fileName: files[currentIdx].name,
        fileFormat: files[currentIdx].name.slice(files[currentIdx].name.lastIndexOf('.') + 1),
        fUrl: '',
      }));

      set((prev: IFile[]) => [...prev, ...fileOption]);
      e.target.value = '';
    }

    return false;
  };

  // 파일 다운로드
  const handleFileDownload = async (fileUrl: string, filename: string) => {
    try {
      await downloadFile(fileUrl, filename);
    } catch (err) {
      console.error('There was an error:', err);
    }
  };

  // 파일 제거
  const onClickFileRemove = (fIndex: number) => {
    const deleteFile = filesState.find((file: IFile) => file.fIndex === fIndex); // 삭제된 파일 값
    if (deleteFile) {
      const deleteId = deleteFile.fUrl !== '' ? [deleteFile?.fIndex] : [];
      const newMap = filesState.filter((file: IFile) => file.fIndex !== fIndex);
      setFilesState(newMap);
      setDeleteFileIndex((prev) => [...prev, ...deleteId]);
    }
  };

  // filesState 초기값(INIT_SCHEDULE)은 파일명이 빈 더미 1건이라 그대로 그리면
  // rowState 반영 effect가 돌기 전 한 프레임 동안 빈 확장자 배지가 보인다. 실제 첨부만 추린다
  const attachedFiles = filesState.filter((el: IFile) => el.fileName);

  const formTypeName = formType === 'attendance' ? t('근태') : formType === 'cdraft' ? t('기안') : t('업무보고');

  if (formSetError) return <IssueGuide />;
  if (mobile && cUserListLoading) {
    return (
      <div className='centered-content'>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );
  }

  if (approvalType === 'approval' && isNewAdd) return <IssueGuide text={`${formTypeName} 항목을 선택하세요`} />;
  return (
    <Root style={{ overflow: !isMobile() ? 'hidden' : 'auto' }}>
      {isApprovalSubmitting && (
        <ApprovalLoadingOverlay role='status' aria-live='assertive'>
          <PulseLoader color='rgb(0, 122, 255)' size='12px' />
          <span>{t('처리 중입니다.')}</span>
        </ApprovalLoadingOverlay>
      )}
      <div className='header flex-flex'>
        <div className='flex-basic'>
          <div className='title'>{t('처리 상태')}</div>
          <SmallTextLabel text={renderApprovalStatus(rowState[statusKey] || '').text} color={renderApprovalStatus(rowState[statusKey] || '').color} />
        </div>
        <div className='flex-end'>
          {showPrintBtn && approvalType === 'approval' && (
            <BtnGhost onClick={onClickPrint}>
              <span className='material-symbols-rounded'>print</span>
              {t('인쇄')}
            </BtnGhost>
          )}
        </div>
      </div>
      {(formType === 'attendance' || formType === 'cdraft') && (
        <>
          {!isMobile() ? (
            <TextInfoBox description='순서의 왼쪽부터 결재가 진행되며, 결재 라인은 총 6개까지만 설정할 수 있습니다.' color='orange' />
          ) : (
            <TextInfoBox description='결재 라인 왼쪽부터 결재 진행, 최대 6명 선택 가능' color='orange' />
          )}
          <div className='header flex-start'>
            <div className='title required'>{t('결재 라인')}</div>
            <div
              className={`flex-basic w100 multi-select ${approvalType === 'approval' || rowState[statusKey] !== '0' ? 'remove_clear' : ''} ${
                isSaveClicked && selectedAttD1List.length === 0 ? 'getBorder' : ''
              }`}
            >
              {approvalType === 'approval' || rowState[statusKey] !== '0' ? (
                <div className='approval-user-line'>
                  {selectedAttD1List.map((user) => (
                    <span key={user.value} className='tag'>
                      {user.label}
                    </span>
                  ))}
                </div>
              ) : (
                <>
                  <MultiSelect options={users} value={selectedAttD1List} setVal={setSelectedAttD1List} placeholder='결재자' maxSelectCount={MAX_SELECT_COUNT} />
                  {approvalType === 'request' && rowState[statusKey] === '0' && (
                    <div className='button' onClick={() => setOpenModal({ type: 'participants', status: true })} role='button' tabIndex={0}>
                      {t('선택')}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      <div className='header flex-start'>
        <div className='title'>{t('수신 / 참조')}</div>
        <div className={`flex-basic w100 multi-select ${approvalType === 'approval' || rowState[statusKey] !== '0' ? 'remove_clear' : ''}`}>
          {approvalType === 'approval' || rowState[statusKey] !== '0' ? (
            <div className='approval-user-line'>
              {selectedAttD2List.map((user) => (
                <span key={user.value} className='tag'>
                  {user.label}
                </span>
              ))}
            </div>
          ) : (
            <>
              <MultiSelect options={users} value={selectedAttD2List} setVal={setSelectedAttD2List} placeholder='수신/참조자' />
              {approvalType === 'request' && rowState[statusKey] === '0' && (
                <div className='button' onClick={() => setOpenModal({ type: 'participants', status: true })} role='button' tabIndex={0}>
                  {t('선택')}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* 잔여 연차일수는 근태 건이 아니라 신청자에게 속한 정보이므로 AttendanceForm 헤더로 옮겼다 */}
      {formType === 'attendance' && (
        <div className='header flex-start'>
          <div className='title'>{t('파일 첨부')}</div>
          <div className='file-wrap'>
            {/**
             * 일정 모달(ScheduleSaveModal)과 동일한 첨부파일 표기.
             * - 이미지 확장자는 썸네일로 그리고, 클릭하면 PhotoProvider 뷰어가 원본을 띄운다.
             * - 그 외 확장자는 FormatImg 로 확장자 배지를 보여준다.
             * imageList 는 뷰어 툴바(파일명·다운로드)가 인덱스로 참조하므로,
             * 아래 map 이 그리는 순서와 같은 순서로 필터링해야 파일명이 어긋나지 않는다.
             */}
            <ImagePreview imageList={attachedFiles.filter((el: IFile) => isImageFile(el.fUrl || el.fileName))}>
              <div className='file-inner'>
                {attachedFiles.map((el: IFile) => (
                  <div key={el.fIndex} className='file-item'>
                    <div className='file' style={{ maxWidth: approvalType === 'request' ? 'calc(100vw - 15rem)' : 'calc(100vw - 12rem)' }}>
                      {isImageFile(el.fUrl || el.fileName) ? (
                        <PhotoImage file={el} fileName={el.fileName} size={38} borderRadius={0.25} />
                      ) : (
                        <FormatImg format={el.fileFormat || 'file'} type='text' textSize='sm' />
                      )}
                      <span>{el.fileName}</span>
                    </div>
                    <div className='flex-basic' style={{ gap: '0.5rem' }}>
                      {el.fUrl !== '' && (
                        <BtnBlue className='file-btn' onClick={() => handleFileDownload(el.fUrl, el.fileName)}>
                          <span className='material-symbols-rounded'>download</span>
                        </BtnBlue>
                      )}
                      {approvalType === 'request' && rowState[statusKey] === '0' && (
                        <BtnRed className='file-btn' onClick={() => onClickFileRemove(el.fIndex)}>
                          <span className='material-symbols-rounded'>close</span>
                        </BtnRed>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ImagePreview>
          </div>
          {approvalType === 'request' && rowState[statusKey] === '0' && (
            <FileUploadButton
              id='files'
              name='files'
              label=''
              ico='upload'
              accept='*'
              multiple='Y'
              handleChange={(e: ChangeEvent<HTMLInputElement>) => onChangeFileUpload(e)}
              allowAllFileTypes='all'
            />
          )}
        </div>
      )}
      <div className='dropzone-wrapper' style={{ overflow: !isMobile() ? 'scroll' : '' }}>
        <div className='dropzone-printable'>
          <DropZone
            cells={cells}
            setCells={setCells}
            logo={logo}
            rowState={rowState}
            userInput={userInput}
            setUserInput={setUserInput}
            formType={formType}
            approvalType={approvalType}
            isSaveClicked={isSaveClicked}
            selectedAttD1List={selectedAttD1List}
            editorContent={editorContent}
            setEditorContent={setEditorContent}
          />
        </div>
        {!isNewAdd && attendanceComments && <ReplyRow rowState={rowState} data={attendanceComments} formType={formType} />}
      </div>
      <PrintContextConsumer>
        {() => (
          <div style={{ display: 'none' }}>
            <div className='dropzone-wrapper print' ref={componentRef}>
              <div className='dropzone-printable'>
                <DropZone
                  cells={cells}
                  setCells={setCells}
                  logo={logo}
                  rowState={rowState}
                  userInput={userInput}
                  setUserInput={setUserInput}
                  formType={formType}
                  approvalType='approval'
                  isSaveClicked={isSaveClicked}
                  selectedAttD1List={selectedAttD1List}
                  className='print'
                  editorContent={editorContent}
                  setEditorContent={setEditorContent}
                />
              </div>
            </div>
          </div>
        )}
      </PrintContextConsumer>

      {approvalType === 'request' && (auth.createAuth || auth.updateAuth || auth.deleteAuth) && (
        <ButtonsWrapper className='end'>
          {!isNewAdd && auth.deleteAuth && (
            <BtnRed type='button' onClick={onClickDelete}>
              {t('삭제')}
            </BtnRed>
          )}
          {(auth.createAuth || auth.updateAuth) && (
            <BtnBlue type='button' onClick={onClickSaveAttendance}>
              {isNewAdd ? t('상신') : t('수정')}
            </BtnBlue>
          )}
        </ButtonsWrapper>
      )}
      {/** 근태결재 컴포넌트이면서 본인의 결재 차례이거나, 최종 결재자가 종결된 건을 수정 중인 경우에만 버튼 렌더링.
          currentUserId 는 서버가 내려주는 현재 결재 대기자라, 완료·반려로 종결된 건에는 빈 값이 온다 */}
      {approvalType === 'approval' && (isMyApprovalTurn || isRevisingApproval) && (
        <ButtonsWrapper className='between'>
          <div className='flex-basic'>
            <BtnRed type='button' disabled={isApprovalSubmitting || myCurrentLstatus === REJECTED} onClick={() => onClickApproval(REJECTED)}>
              {t('반려')}
            </BtnRed>
            <BtnYellow type='button' disabled={isApprovalSubmitting || myCurrentLstatus === PENDING_APPROVAL} onClick={() => onClickApproval(PENDING_APPROVAL)}>
              {t('보류')}
            </BtnYellow>
          </div>
          <div className='flex-basic'>
            {isRevisingApproval && (
              <BtnGhost type='button' disabled={isApprovalSubmitting} onClick={() => setIsRevisingApproval(false)}>
                {t('취소')}
              </BtnGhost>
            )}
            <BtnBlue type='button' disabled={isApprovalSubmitting || myCurrentLstatus === APPROVED} onClick={() => onClickApproval(APPROVED)}>
              {t('승인')}
            </BtnBlue>
          </div>
        </ButtonsWrapper>
      )}
      {canReviseApproval && !isMyApprovalTurn && !isRevisingApproval && (
        <ButtonsWrapper className='end'>
          <BtnBlue type='button' onClick={() => setIsRevisingApproval(true)}>
            {t('수정')}
          </BtnBlue>
        </ButtonsWrapper>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'participants' && <ApprovalModal openModal={openModal} setOpenModal={setOpenModal} lGubun='2' getLineList={getLineList} />}
        {openModal.status && openModal.type === 'delete' && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}
      </Portal>
    </Root>
  );
};

export default Form;

const ApprovalLoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.72);
  color: var(--text-primary);
  font-size: 0.95rem;
`;

const Root = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  /* overflow: hidden; */
  justify-content: flex-start;

  .header {
    padding: 0.4rem 0.75rem; // 상하 패딩 축소
    min-height: 2.75rem; // 고정 height 대신 min-height: 내용이 큰 행(파일첨부 .file-wrap 3.5rem 등)은 늘어나 깨지지 않도록
    flex-shrink: 0; // Root가 height:100%+overflow:hidden인 flex column이라, 이게 없으면 내용이 많을 때 행이 압축되어 .file-wrap이 다음 행으로 넘침
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    color: var(--text-primary);
    display: flex;
    gap: 0.5rem;
    &:nth-child(n + 2) {
      border-top: 1px solid var(--outline);
    }
    .title {
      flex-shrink: 0;
      width: fit-content;
      min-width: 4.5rem;
      font-weight: 600;
      margin-right: 0.5rem;
    }

    .file-wrap {
      width: 100%;
      // 썸네일/확장자 배지(2.4rem)가 들어가면서 한 행이 2rem을 넘는다.
      // 2건까지는 그대로 보이고 그 이상은 이 영역 안에서만 세로 스크롤한다
      max-height: 5.5rem;
      overflow-y: auto;
      border-radius: 0.5rem;
      padding: 0; // 상하 패딩 제거

      .file-inner {
        display: flex;
        flex-direction: column !important;
        width: 100%;
        gap: 0.5rem;
      }

      .file-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 0 0.5rem;

        &:hover {
          background-color: var(--tonal);
        }
      }

      .file {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        min-width: 0; // 파일명 span이 min-content 아래로 줄어들 수 있어야 ellipsis가 동작한다

        // 썸네일(figure) · 확장자 배지(div)는 파일명이 길어도 찌그러지지 않게 고정 크기를 유지
        > figure,
        > div {
          flex-shrink: 0;
          margin: 0;
        }

        > figure {
          cursor: pointer; // 클릭하면 원본 뷰어가 열린다
        }

        span {
          font-size: 0.875rem;
          color: var(--text-primary);

          white-space: nowrap;
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          width: 100%;
          min-width: 0;
        }
      }

      .file-btn {
        width: 2rem;
        height: 2rem;
        padding: 0 0.5rem;

        span.material-symbols-rounded {
          font-variation-settings: 'FILL' 1, 'wght' 400;
          font-size: 1.25rem;
        }
      }
    }

    > div > label > .flex-box > .material-symbols-rounded {
      margin-right: 0;
    }

    // 파일첨부 업로드 버튼(FileUploadButton 기본 height 2.5rem)을 다른 행 요소(2rem)와 맞춰 파일첨부 행 높이를 다른 행과 동일하게
    > div:has(> input[type='file']) {
      height: 2rem;
    }

    @media screen and (max-width: 767px) {
      padding: 0.25rem;
    }
  }

  .approval-user-line {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    overflow-x: auto;

    .tag {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 0.5rem;
      height: 28px;
      border-radius: 2.5px;
      color: var(--selected-primary);
      border: 1px solid var(--selected-primary);
      background-color: var(--tonal-blue);
      min-width: fit-content;
    }
  }

  .tagContainer {
    user-select: none;
    background-color: var(--tonal-light);
    padding: 0.5rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.2rem 0.5rem;
    width: 100%;
    height: 8rem;
    overflow: auto;
  }

  .button {
    flex-shrink: 0;
    user-select: none;
    cursor: pointer;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--filled-slate-deep);
    color: var(--bw-inverse);
    border-radius: 0.25rem;
    padding: 0 0.75rem;
    margin: 0.5rem;
    font-size: 0.875rem;
    font-weight: 400;
    &:hover {
      outline: 0.25rem solid var(--outline);
      &:active {
        background-color: var(--filled-slate);
      }
    }
  }

  .scroll-container {
    flex-grow: 1;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    overflow-y: hidden;
    white-space: nowrap;
  }

  .multi-select {
    height: 2rem; // 우측 '선택' 버튼(.button height: 2rem)과 높이 통일
    flex-grow: 1;
    min-width: 0; // 필수: flex-grow가 있을 때 오버플로우를 위해 필요

    &.getBorder {
      > :first-child {
        border: solid 2px red !important;
        border-radius: 5px;
      }
    }

    &.remove_clear {
      .select__multi-value__remove {
        display: none !important;
      }
    }

    .select__control {
      box-sizing: border-box; // 테두리(1px)가 높이에 더해져 버튼보다 커지는 것 방지
      // 우측 '선택' 버튼(2rem)과 높이 통일. react-select 기본 control(min-height 38px)/내부 콘텐츠가 32px를 넘기므로 height를 하드 고정.
      // 하드 고정 시 내부 grid가 클램프되어 placeholder가 아래로 밀리는 문제는 아래 value-container를 flex 중앙정렬로 강제해 해결.
      min-height: 2rem;
      height: 2rem;
      max-height: 2rem;
      background-color: var(--tonal-light);
      border-color: var(--outline);

      &:hover {
        border-color: var(--outline);
      }

      .select__placeholder {
        color: var(--text-primary);
        font-size: 0.875rem;
      }
    }

    .select__value-container {
      // react-select 기본은 display:grid라 컨트롤을 2rem로 하드 고정하면 placeholder 세로정렬이 틀어짐.
      // flex로 바꿔 자식(placeholder/태그)을 세로 중앙정렬. (단, placeholder 자체는 flex로 만들지 말 것 - 텍스트가 잘림)
      display: flex;
      align-items: center;
      height: 100%;
      padding: 0 8px; // 상하 패딩 제거: 컨트롤 높이 2rem 안에 태그가 들어가도록
      flex-wrap: nowrap; // 줄바꿈 방지
      overflow-x: auto; // 가로 스크롤 추가
    }

    // placeholder/입력영역을 full-height로 강제하지 않음(강제 시 컨트롤 하드 고정과 겹쳐 placeholder가 아래로 밀림).
    // display는 react-select 기본값 유지(flex로 바꾸면 placeholder 텍스트가 잘림)
    .select__placeholder,
    .select__single-value,
    .select__input-container {
      height: auto;
      margin: 0;
      padding: 0;
    }

    // 드롭다운/클리어 아이콘 패딩 축소: 기본 패딩(8px)+아이콘(20px)=36px라 컨트롤이 2rem을 넘김.
    // react-select 실제 클래스명(dropdown/clear-indicator)으로 타깃해야 적용됨
    .select__indicators {
      height: 2rem;
    }
    .select__indicator,
    .select__dropdown-indicator,
    .select__clear-indicator {
      padding: 2px;
    }
    .select__indicator svg,
    .select__dropdown-indicator svg,
    .select__clear-indicator svg {
      width: 18px;
      height: 18px;
    }
    .select__indicator-separator {
      margin: 4px 0;
    }

    .select__multi-value {
      box-sizing: border-box; // 테두리(1px) 포함 22px로 계산되어 세로 중앙 어긋남 방지
      height: 22px; // 뱃지 높이 축소 (컨트롤 2rem 안에 여유있게)
      display: flex;
      align-items: center;
      align-self: center; // value-container 안에서 세로 중앙 고정
      transform: translateY(-1px); // 형제 입력영역(폰트 14px)과의 높이/베이스라인 차로 뱃지가 미세하게 내려가 보이는 것 광학 보정
      margin: 0 2px;
      flex-shrink: 0; // 태그가 줄어들지 않도록 설정
      color: var(--selected-primary);
      background-color: var(--tonal-blue);
      border-radius: 2.5px;
      border: 1px solid;
    }

    .select__multi-value__label {
      color: var(--selected-primary);
      display: flex;
      align-items: center;
      padding: 1px 6px;
      font-size: 0.75rem; // 뱃지 폰트 축소
      font-weight: 500;
    }

    .select__multi-value__remove {
      display: flex;
      align-items: center;
      padding: 0 4px;
      height: 100%;
      cursor: pointer;
    }

    .button {
      // 상하 margin 제거: .multi-select 행이 버튼 margin(0.5rem)만큼 커져(컨트롤 높이 초과) placeholder가 아래로 밀리는 문제 방지. 좌측 간격만 유지
      margin: 0 0 0 0.5rem;
      height: 2rem;
    }
  }

  .dropzone-wrapper {
    /* overflow: scroll; */

    &.print {
      background-color: #fff;
    }

    @media screen and (max-width: 767px) {
      // 격자 배치용 압축. 한 줄 배치 항목(.stacked)은 ApplicationFormItems 스타일을 따름
      .input:not(.stacked) {
        padding: 0.2rem;

        & .label {
          width: fit-content;
          padding-right: 0.5rem;
        }
      }
    }
  }

  // 한 줄 배치: 격자에서 왼쪽 라벨 칸 자리를 비우려던 에디터 오른쪽 이동(4rem)을 풀고 폭 전체 사용
  .dropzone-stack .subcd_18 > .editor > .editor-wrapper .ck-editor {
    width: 100%;
    right: 0;
  }

  .subcd_18 > .editor > .editor-wrapper {
    .ck-editor {
      width: calc(100% - 4rem);
      right: -4rem;
      border-radius: 6px;
      /* border-radius: 0 6px 6px 0; */

      .ck-content {
        border-radius: 0 0 6px 6px;
      }

      .ck-sticky-panel__content {
        border-radius: 6px 6px 0 0;
      }
    }

    > div {
      .ck-editor__main > div {
        height: 38rem;
      }
    }
  }
`;

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;

  &.end {
    justify-content: flex-end;
  }

  &.between {
    justify-content: space-between;
    > div {
      gap: 0.5rem;
    }
  }

  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
`;
